import type { IntelData } from "./translate";

export interface CommandCenterDataReadinessRow {
  symbol: string;
  inputIntegrity: string;
  feedFreshness: string;
  dataStatus: string;
  ctoAction: string;
}

const PRICE_FRESH_MAX_SEC = 600;
const ORDER_FLOW_FRESH_MAX_SEC = 180;
const VOLUME_FLOW_FRESH_MAX_SEC = 900;

function hasValue(value: unknown): boolean {
  return value !== null && value !== undefined && value !== "";
}

export function buildInputIntegrity(data: IntelData | undefined): string {
  if (!data) return "0/5 Blocked: no live inputs";

  const checks = [
    { label: "live", ok: data.data_status === "live" },
    { label: "price", ok: typeof data.current_price === "number" },
    { label: "trend", ok: hasValue(data.kronos_direction) || hasValue(data.kronos_4h_direction) || hasValue(data.kronos_hourly_direction) || hasValue(data.hmm_regime) },
    { label: "flow", ok: hasValue(data.institutional_direction) || hasValue(data.order_flow_status) },
    { label: "volume/squeeze", ok: hasValue(data.cmf_20) || hasValue(data.mfi_14) || hasValue(data.obv_divergence_10) || hasValue(data.squeeze_on) || hasValue(data.squeeze_1h_status) || hasValue(data.squeeze_4h_status) || hasValue(data.squeeze_daily_status) },
  ];
  const passed = checks.filter((check) => check.ok).length;
  const missing = checks.filter((check) => !check.ok).map((check) => check.label);
  const outlierSources = data.current_price_outlier_sources?.trim();
  const suffix = outlierSources ? ` · price outlier excluded: ${outlierSources}` : "";

  if (passed === checks.length) return `${passed}/${checks.length} Ready${suffix}`;
  if (passed >= 3) return `${passed}/${checks.length} Watch: ${missing.join(", ")}${suffix}`;
  return `${passed}/${checks.length} Blocked: ${missing.join(", ")}${suffix}`;
}

function formatAge(seconds: number | undefined): string {
  if (typeof seconds !== "number" || !Number.isFinite(seconds) || seconds < 0) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  if (minutes < 60) return remainingSeconds === 0 ? `${minutes}m` : `${minutes}m ${remainingSeconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes === 0 ? `${hours}h` : `${hours}h ${remainingMinutes}m`;
}

export function buildFeedFreshness(data: IntelData | undefined): string {
  if (!data) return "Price — · OF — · Vol —";
  return [
    `Price ${formatAge(data.current_price_age_sec)}`,
    `OF ${formatAge(data.order_flow_age_sec)}`,
    `Vol ${formatAge(data.volume_flow_age_sec)}`,
  ].join(" · ");
}

function isStaleAge(value: number | undefined, maxSeconds: number): boolean {
  return typeof value === "number" && Number.isFinite(value) && value > maxSeconds;
}

export function buildCtoDataAction(data: IntelData | undefined): string {
  if (!data || data.data_status === "missing") return "Block: no live inputs";
  if (data.data_status === "stale") return "Block: refresh live snapshot";
  if (data.current_price_disagreement === true) return "Block: price consensus failed";
  if (data.current_price_status === "missing" || typeof data.current_price !== "number") return "Block: price missing";
  if (data.current_price_status === "stale" || isStaleAge(data.current_price_age_sec, PRICE_FRESH_MAX_SEC)) return "Block: price stale";
  if (data.order_flow_status === "stale" || isStaleAge(data.order_flow_age_sec, ORDER_FLOW_FRESH_MAX_SEC)) return "Watch: order flow stale";
  if (data.volume_flow_status === "stale" || isStaleAge(data.volume_flow_age_sec, VOLUME_FLOW_FRESH_MAX_SEC)) return "Watch: volume flow stale";
  if (data.current_price_outlier_sources?.trim()) return "Audit: consensus ok, verify excluded hub";
  if (!hasValue(data.kronos_direction) && !hasValue(data.kronos_4h_direction) && !hasValue(data.kronos_hourly_direction) && !hasValue(data.hmm_regime)) return "Watch: trend context missing";
  if (!hasValue(data.institutional_direction) && !hasValue(data.order_flow_status)) return "Watch: order-flow context missing";
  return "Ready: data line decision-grade";
}

export function buildCommandCenterDataReadinessRows(
  symbols: readonly string[],
  intelMap: Record<string, IntelData | undefined>,
): CommandCenterDataReadinessRow[] {
  return symbols.map((symbol) => {
    const data = intelMap[symbol];
    return {
      symbol,
      inputIntegrity: buildInputIntegrity(data),
      feedFreshness: buildFeedFreshness(data),
      dataStatus: data?.data_status === "live" ? "Live" : data?.data_status === "stale" ? "Stale" : "No data",
      ctoAction: buildCtoDataAction(data),
    };
  });
}
